/**
 * The network-health cockpit's addresses — `docs/adr/9046`. The URL is the state (G2.2, CLAUDE.md
 * §3.4): the run (`?runId=`), the grouping (`?by=`), the department a level was reached through
 * (`?department=`), the problem states level 1 is narrowed to (`?state=out,broken`, the KPI cards'
 * links, 9046); the store and the article are path segments.
 *
 *   L0  /inseason/network-health                                     the market
 *   L1  /inseason/network-health/by?by=department_family|store|article  where, one row per group
 *   L2  /inseason/network-health/article/:articleKey                  one article, every store
 *       /inseason/network-health/store/:storeKey                      one store, every article («Raggruppa per Negozio»)
 *   L3  /inseason/network-health/store/:storeKey/article/:articleKey  one store × article, every size
 *
 * Every intervention is a link to its queue, already filtered (brief part 4 §1.3, §7): the
 * replenishment decisions page on the store and the article (`?f.store=&f.article=`, its own
 * filters — it has no size filter), the move run's list of moves to decide on the destination or
 * origin and the article with the line selected (`?moveRunId=&f.destination=`/`f.origin=`
 * `&f.article=&row=`, bata-platform ADR 6055 amended 2026-10-31) — or, for a run recorded before the
 * move run, «Perché» of its transfers (history: `?f.donor=`/`?f.recipient=&row=`) — the alert group of
 * the rule at the store with the article, and the price decisions of the run. No decision is taken
 * here.
 *
 * Pure, except the hook at the end.
 */

import { RUN_ID_PARAM } from '../scope';
import { storeArticleRowId } from '../store-article-rows';

import { PROBLEM_STATES, readsTransfers, type InterventionRuns, type NetworkHealthDimension, type ProblemState } from '@/api/pending/network-health';

export const NETWORK_HEALTH_PATH = '/inseason/network-health';
export const NETWORK_HEALTH_GROUPS_PATH = `${NETWORK_HEALTH_PATH}/by`;
export const NETWORK_HEALTH_ARTICLE_PATTERN = `${NETWORK_HEALTH_PATH}/article/:articleKey`;
export const NETWORK_HEALTH_STORE_PATTERN = `${NETWORK_HEALTH_PATH}/store/:storeKey`;
export const NETWORK_HEALTH_SIZES_PATTERN = `${NETWORK_HEALTH_PATH}/store/:storeKey/article/:articleKey`;

export const BY_PARAM = 'by';
export const DEPARTMENT_PARAM = 'department';
export const VIEW_PARAM = 'view';
/**
 * The states level 1 is narrowed to: ONE key holding the codes comma-separated (`?state=out,broken`),
 * as `?optionKeys=` does — the shell's scope chips and its carry between screens keep one value per
 * key, so a repeated key would be read back as its last value. The read to the platform repeats it
 * (`state=out&state=broken`, `networkHealthParams`).
 */
export const STATE_PARAM = 'state';

/** The groupings level 1 offers — «Reparto | Negozio | Articolo» (brief part 4 §3). */
export const LEVEL_ONE_DIMENSIONS = ['department_family', 'store', 'article'] as const satisfies readonly NetworkHealthDimension[];
export type LevelOneDimension = (typeof LEVEL_ONE_DIMENSIONS)[number];

/** Level 0's two views — «Oggi | Per settimana» (§2). */
export type NetworkView = 'today' | 'weeks';

export interface NetworkHealthScope {
  readonly runId: string | null;
  readonly by: LevelOneDimension;
  readonly department: string | null;
  readonly view: NetworkView;
  /** The problem states level 1 shows groups for, in the cockpit's order; empty: every group. */
  readonly states: readonly ProblemState[];
}

const text = (params: URLSearchParams, key: string): string | null => {
  const value = params.get(key)?.trim() ?? '';
  return value === '' ? null : value;
};

/**
 * The states a search string names: `?state=out,broken`, or the key repeated; the codes the
 * platform knows (`low`, `out`, `broken`) in the cockpit's order, once each — anything else dropped.
 */
export function readStates(params: URLSearchParams): readonly ProblemState[] {
  const named = new Set(params.getAll(STATE_PARAM).flatMap((value) => value.split(',')).map((code) => code.trim()));
  return PROBLEM_STATES.filter((state) => named.has(state));
}

/** The scope a search string names. `?by=` defaults to the department (the brief's first option). */
export function readNetworkHealthScope(search: string): NetworkHealthScope {
  const params = new URLSearchParams(search);
  const by = text(params, BY_PARAM);
  return {
    runId: text(params, RUN_ID_PARAM),
    by: (LEVEL_ONE_DIMENSIONS as readonly string[]).includes(by ?? '') ? (by as LevelOneDimension) : 'department_family',
    department: text(params, DEPARTMENT_PARAM),
    view: text(params, VIEW_PARAM) === 'weeks' ? 'weeks' : 'today',
    states: readStates(params),
  };
}

export interface NetworkHealthPath {
  readonly level: 0 | 1 | 2 | 3;
  readonly storeKey: string | null;
  readonly articleKey: string | null;
}

const decode = (segment: string | undefined): string | null => {
  if (segment === undefined || segment === '') return null;
  try { return decodeURIComponent(segment); } catch { return segment; }
};

/** Which level a pathname is, and the keys it names. */
export function readNetworkHealthPath(pathname: string): NetworkHealthPath {
  const base = NETWORK_HEALTH_PATH.split('/').filter(Boolean);
  const segments = pathname.split('/').filter(Boolean);
  if (!base.every((segment, index) => segments[index] === segment)) return { level: 0, storeKey: null, articleKey: null };
  const rest = segments.slice(base.length);
  if (rest[0] === 'by') return { level: 1, storeKey: null, articleKey: null };
  if (rest[0] === 'article') return { level: 2, storeKey: null, articleKey: decode(rest[1]) };
  if (rest[0] === 'store') {
    const storeKey = decode(rest[1]);
    return rest[2] === 'article'
      ? { level: 3, storeKey, articleKey: decode(rest[3]) }
      : { level: 2, storeKey, articleKey: null };
  }
  return { level: 0, storeKey: null, articleKey: null };
}

/** A query string from pairs, the `null` ones left out — `?` included, or `''`. */
function query(pairs: Readonly<Record<string, string | null>>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(pairs)) if (value !== null) params.set(key, value);
  // The list in `?state=out,broken` keeps its commas: legal in a query, and a link people read.
  const next = params.toString().replace(/(^|&)(state=[^&]*)/, (_, lead: string, pair: string) => `${lead}${pair.replaceAll('%2C', ',')}`);
  return next === '' ? '' : `?${next}`;
}

export function networkHref(runId: string | null): string {
  return `${NETWORK_HEALTH_PATH}${query({ [RUN_ID_PARAM]: runId })}`;
}

/** The `?state=` value of a list of states: the known ones, once each, in the cockpit's order — or `null`. */
function statesValue(states: readonly ProblemState[]): string | null {
  const kept = PROBLEM_STATES.filter((state) => states.includes(state));
  return kept.length === 0 ? null : kept.join(',');
}

/**
 * Level 1. `states` narrows it to the groups with a cell in those states (the KPI cards, 9046); the
 * links down from a filtered list carry it so «back» returns to the filtered list.
 */
export function groupsHref(runId: string | null, by: LevelOneDimension, department: string | null = null, states: readonly ProblemState[] = []): string {
  return `${NETWORK_HEALTH_GROUPS_PATH}${query({ [BY_PARAM]: by, [DEPARTMENT_PARAM]: department, [STATE_PARAM]: statesValue(states), [RUN_ID_PARAM]: runId })}`;
}

/** Level 2, an article — `?by=` (and the department) kept so the breadcrumb climbs back to them. */
export function articleHref(articleKey: string, runId: string | null, by: LevelOneDimension, department: string | null = null, states: readonly ProblemState[] = []): string {
  return `${NETWORK_HEALTH_PATH}/article/${encodeURIComponent(articleKey)}${query({ [BY_PARAM]: by, [DEPARTMENT_PARAM]: department, [STATE_PARAM]: statesValue(states), [RUN_ID_PARAM]: runId })}`;
}

/** Level 2, a store («Raggruppa per Negozio», §7). */
export function storeHref(storeKey: string, runId: string | null, by: LevelOneDimension = 'store', department: string | null = null, states: readonly ProblemState[] = []): string {
  return `${NETWORK_HEALTH_PATH}/store/${encodeURIComponent(storeKey)}${query({ [BY_PARAM]: by, [DEPARTMENT_PARAM]: department, [STATE_PARAM]: statesValue(states), [RUN_ID_PARAM]: runId })}`;
}

/** Level 3. */
export function sizesHref(storeKey: string, articleKey: string, runId: string | null, by: LevelOneDimension, department: string | null = null, states: readonly ProblemState[] = []): string {
  return `${NETWORK_HEALTH_PATH}/store/${encodeURIComponent(storeKey)}/article/${encodeURIComponent(articleKey)}${query({ [BY_PARAM]: by, [DEPARTMENT_PARAM]: department, [STATE_PARAM]: statesValue(states), [RUN_ID_PARAM]: runId })}`;
}

/** The row of level 1 a group opens: a department lists its articles; a store and an article open level 2. */
export function groupRowHref(by: LevelOneDimension, key: string, runId: string | null, department: string | null, states: readonly ProblemState[] = []): string {
  if (by === 'department_family') return groupsHref(runId, 'article', key, states);
  if (by === 'store') return storeHref(key, runId, 'store', department, states);
  return articleHref(key, runId, 'article', department, states);
}

// ── the queues an intervention opens ─────────────────────────────────────────

/**
 * A proposal's «Perché» — one store × article of the run, `?row=<store>|<article>` — as a transfer
 * opens its own (audit 2026-10-21, F9). Its «Decide in …» link opens the same row on the
 * replenishment decisions page (Riassortimento › Approvazioni in sospeso), the queue's one home.
 */
export const PROPOSAL_WHY_PATH = '/inseason/allocation-why';
/** «Perché» of the transfers — filterable by donor and recipient, a transfer selectable by `?row=`. History only. */
export const TRANSFERS_QUEUE_PATH = '/inseason/transfer-why';
/**
 * The move run's one list of moves to decide (Compacting › «Approvazioni in attesa») — ch02's path,
 * spelled here: filterable by `f.destination`, `f.origin`, `f.article`, `f.purpose`, `f.state` (its
 * `urlParams`), a line selectable by `?row=`.
 */
export const MOVES_QUEUE_PATH = '/inseason/compacting-moves';
/** The move list's run key (`../compacting/move-scope.ts`) — written into a link here, never read. */
export const MOVE_RUN_ID_KEY = 'moveRunId';
/** The price decisions of a run. */
export const PRICE_QUEUE_PATH = '/inseason/markdown/approvals';
/** One alert group's page (ADR 9040) — ch00's path, spelled here: a feature may not import another. */
export const ALERT_GROUP_PATH = '/foundations/exceptions/group';
/** The cycles of the market — «Vai a Cicli» (§6). */
export const CYCLES_PATH = '/inseason/cycles';

export function proposalWhyHref(runId: string, storeKey: string, articleKey: string): string {
  return `${PROPOSAL_WHY_PATH}${query({ [RUN_ID_PARAM]: runId, row: storeArticleRowId(storeKey, articleKey) })}`;
}

export function transferQueueHref(runId: string, direction: 'in' | 'out', storeKey: string, transferId: string | null): string {
  return `${TRANSFERS_QUEUE_PATH}${query({
    [RUN_ID_PARAM]: runId,
    [direction === 'in' ? 'f.recipient' : 'f.donor']: storeKey,
    row: transferId,
  })}`;
}

/**
 * One move in the move run's list, filtered to the store on its side and the article, with the line
 * selected: `direction` `in` filters by destination, `out` by origin. The list shows the moves to
 * decide, so its count is the store × article's undecided moves on that side (`docs/adr/9069`).
 */
export function movesQueueHref(moveRunId: string, direction: 'in' | 'out', storeKey: string, articleKey: string, lineId: string | null): string {
  return `${MOVES_QUEUE_PATH}${query({
    [MOVE_RUN_ID_KEY]: moveRunId,
    [direction === 'in' ? 'f.destination' : 'f.origin']: storeKey,
    'f.article': articleKey,
    row: lineId,
  })}`;
}

/** The decisions after which a move is no longer a row of the moves to decide. */
const DECIDED: ReadonlySet<string> = new Set(['approve', 'adjust', 'defer', 'reject']);

/**
 * Where one store-to-store leg opens, or `null` when no list holds it: a run recorded before the move
 * run opens its transfer in «Perché» (history); a move opens its line in the moves to decide, unless it
 * is decided — the list would not show it, so it is said in words, never linked (`docs/adr/9069`); a
 * run whose moves are in no table has no leg to open. Pure.
 */
export function legHref(
  runs: InterventionRuns,
  direction: 'in' | 'out',
  storeKey: string,
  articleKey: string,
  leg: { readonly transferId: string; readonly disposition: string | null },
): string | null {
  if (readsTransfers(runs)) return transferQueueHref(runs.transfers, direction, storeKey, leg.transferId);
  if (runs.moves === null) return null;
  if (leg.disposition !== null && DECIDED.has(leg.disposition)) return null;
  return movesQueueHref(runs.moves, direction, storeKey, articleKey, leg.transferId);
}

export function alertGroupHref(ruleId: string, market: string, storeKey: string, articleKey: string): string {
  return `${ALERT_GROUP_PATH}${query({ rule: ruleId, by: 'store', key: storeKey, country: market, article: articleKey, queue: 'all', state: 'open' })}`;
}

export function priceQueueHref(runId: string): string {
  return `${PRICE_QUEUE_PATH}${query({ [RUN_ID_PARAM]: runId })}`;
}
